import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Link2, Copy, RotateCcw, Send, Mail } from "lucide-react";
import api, { apiErr, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, adminInput } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import EmailLinkDialog from "@/components/admin/EmailLinkDialog";

const REQUEST_ROLES = ["owner", "host", "resort_manager", "finance", "front_desk"];
const REFUND_ROLES = ["owner", "host", "finance"];

function StatusPill({ status }) {
  const good = status === "Paid";
  const warn = ["Pending", "Processing", "Initiated"].includes(status);
  const bad = ["Failed", "Cancelled", "Expired", "Disputed"].includes(status);
  const cls = good ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"
    : bad ? "text-red-300 border-red-400/40 bg-red-400/10"
    : warn ? "text-amber-300 border-amber-400/40 bg-amber-400/10"
    : "text-muted-foreground border-white/20 bg-white/5";
  return <span className={`status-pill border ${cls}`} data-testid="online-txn-status">{status}</span>;
}

function RequestDialog({ rid, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ purpose: "balance", amount: "", description: "", due_date: "", guest_message: "", internal_notes: "" });
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/reservations/${rid}/payment-request`, {
        purpose: f.purpose, amount: f.amount ? parseFloat(f.amount) : undefined,
        description: f.description || undefined, due_date: f.due_date || undefined,
        guest_message: f.guest_message || undefined, internal_notes: f.internal_notes || undefined,
        origin_url: window.location.origin,
      });
      setLink(data.checkout_url);
      toast.success("Secure payment request created.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setLink(""); } }}>
      <DialogTrigger asChild>
        <button className="btn-ghost-gold px-4 py-2 text-sm flex items-center gap-2" data-testid="create-payment-request-btn"><Send size={15} /> Create Secure Payment Request</button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-white/10">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Create Secure Payment Request</DialogTitle></DialogHeader>
        {link ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Present or send this secure payment link to the guest:</p>
            <div className="flex gap-2">
              <input readOnly value={link} className={adminInput} data-testid="payment-request-link" />
              <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied."); }} className="btn-ghost-gold px-3" data-testid="copy-payment-link"><Copy size={15} /></button>
            </div>
            <p className="text-[11px] text-muted-foreground">The guest enters card details only on the secure payment page. The folio updates automatically once payment is confirmed.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <select className={adminInput} value={f.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="payment-request-purpose">
              <option value="deposit">Required Deposit</option>
              <option value="balance">Full Outstanding Balance</option>
              <option value="custom">Approved Custom Amount</option>
            </select>
            {f.purpose === "custom" && (
              <input className={adminInput} type="number" placeholder="Amount (USD)" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="payment-request-amount" />
            )}
            <input className={adminInput} placeholder="Description (guest-facing, optional)" value={f.description} onChange={(e) => set("description", e.target.value)} />
            <input type="date" className={adminInput} value={f.due_date} onChange={(e) => set("due_date", e.target.value)} />
            <textarea className={adminInput} rows={2} placeholder="Message to guest (optional)" value={f.guest_message} onChange={(e) => set("guest_message", e.target.value)} />
            <textarea className={adminInput} rows={2} placeholder="Internal notes (optional)" value={f.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} />
            <button onClick={save} disabled={saving} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="payment-request-save">{saving ? <Loader2 className="animate-spin" size={15} /> : null} Create Request</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RefundDialog({ txn, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const refundable = txn.amount_cents - (txn.refunded_cents || 0);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/payments/transactions/${txn.id}/refund`, {
        amount: f.amount ? parseFloat(f.amount) : undefined, reason: f.reason || undefined });
      toast.success("Refund processed."); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-xs text-red-300 hover:text-red-200 flex items-center gap-1" data-testid={`refund-btn-${txn.id}`}><RotateCcw size={12} /> Refund</button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-white/10">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Refund Payment</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Original amount</span><span>{money(txn.amount)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Already refunded</span><span>{money(txn.refunded)}</span></div>
          <div className="flex justify-between font-medium"><span>Refundable</span><span className="text-gold">{money(refundable / 100)}</span></div>
          <input className={adminInput} type="number" placeholder="Refund amount (blank = full)" value={f.amount} onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))} data-testid="refund-amount" />
          <textarea className={adminInput} rows={2} placeholder="Reason" value={f.reason} onChange={(e) => setF((p) => ({ ...p, reason: e.target.value }))} data-testid="refund-reason" />
          <button onClick={save} disabled={saving} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="refund-save">{saving ? <Loader2 className="animate-spin" size={15} /> : null} Confirm Refund</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SecurePayment({ rid }) {
  const { user } = useAuth();
  const role = user?.role;
  const [txns, setTxns] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [emailFor, setEmailFor] = useState(null);
  const canRequest = REQUEST_ROLES.includes(role);
  const canRefund = REFUND_ROLES.includes(role);

  const load = () => {
    api.get(`/admin/reservations/${rid}/online-transactions`).then((r) => setTxns(r.data)).catch(() => {});
    api.get(`/admin/reservations/${rid}/payment-requests`).then((r) => setReqs(r.data)).catch(() => {});
    api.get(`/admin/payments/disputes`).then((r) => setDisputes((r.data || []).filter((d) => d.reservation_id === rid))).catch(() => {});
  };
  useEffect(() => { load(); }, [rid]);

  return (
    <Panel title="Secure Payment" action={canRequest ? <RequestDialog rid={rid} onDone={load} /> : null}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
        <ShieldCheck size={14} className="text-gold" /> Card details are captured only on the secure payment page — never stored in The D.
      </div>
      {reqs.filter((q) => ["Open", "Active"].includes(q.status)).length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2">Open Payment Requests</p>
          {reqs.filter((q) => ["Open", "Active"].includes(q.status)).map((q) => (
            <div key={q.id} className="flex justify-between items-center text-xs py-1.5 border-t border-white/5" data-testid={`payment-request-${q.id}`}>
              <span>{q.purpose_label} · {money(q.amount)}</span>
              <div className="flex items-center gap-3">
                {canRequest && <button onClick={() => setEmailFor(q.id)} className="text-pool hover:text-pool/80 flex items-center gap-1" data-testid={`email-link-${q.id}`}><Mail size={12} /> Email Secure Payment Link</button>}
                {q.checkout_url && (
                  <button onClick={() => { navigator.clipboard.writeText(q.checkout_url); toast.success("Link copied."); }} className="text-gold hover:text-gold/80 flex items-center gap-1"><Link2 size={12} /> Copy link</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {disputes.length > 0 && (
        <div className="mb-4 rounded-md border border-red-400/40 bg-red-400/5 p-3" data-testid="payment-disputes">
          <p className="text-[10px] tracking-label uppercase text-red-300 mb-2">Payment Disputes</p>
          {disputes.map((d) => (
            <div key={d.id} className="text-xs py-1.5 border-t border-white/5" data-testid={`dispute-${d.id}`}>
              <div className="flex justify-between"><span>Disputed {money((d.amount_cents || 0) / 100)} · {d.reason || "—"}</span><span className="text-red-300">{d.status}</span></div>
              {d.response_deadline && <p className="text-muted-foreground">Response deadline: {d.response_deadline}</p>}
            </div>
          ))}
        </div>
      )}
      {txns.length === 0 ? (
        <p className="text-sm text-muted-foreground">No online payments yet.</p>
      ) : (
        <div className="space-y-1">
          <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-1">Online Transactions</p>
          {txns.map((t) => (
            <div key={t.id} className="flex justify-between items-center text-xs py-2 border-t border-white/5" data-testid={`online-txn-${t.id}`}>
              <div>
                <span>{fmtDate(t.created_at)} · Online Payment {t.card_last4 ? `(${t.card_brand ? t.card_brand + " " : ""}••${t.card_last4})` : ""}</span>
                <div className="text-muted-foreground">{t.purpose_label}{parseFloat(t.refunded) > 0 ? ` · refunded ${money(t.refunded)}` : ""}</div>
              </div>
              <div className="flex items-center gap-3">
                <span>{money(t.amount)}</span>
                <StatusPill status={t.status} />
                {canRefund && t.status === "Paid" && (t.amount_cents - (t.refunded_cents || 0)) > 0 && <RefundDialog txn={t} onDone={load} />}
              </div>
            </div>
          ))}
        </div>
      )}
      <EmailLinkDialog requestId={emailFor} open={!!emailFor} onOpenChange={(o) => { if (!o) setEmailFor(null); }} onSent={load} />
    </Panel>
  );
}
